import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Image } from "@/components/ui/image";
import OutdoorFaqList from "@/components/outdoor/OutdoorFaqList";
import {
  OUTDOOR_LIVING_HERO,
  OUTDOOR_LIVING_SECTIONS,
  OUTDOOR_LIVING_FAQS,
  OUTDOOR_LIVING_TOWNS,
} from "@/lib/outdoorLiving";

export default function OutdoorLivingSpaceContractor() {
  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="container-luxe pt-16 md:pt-24 pb-12 md:pb-16">
        <Link to="/services" className="inline-flex items-center gap-2 text-muted-foreground text-[11px] font-bold tracking-[0.2em] uppercase mb-8 hover:text-accent">
          <ArrowLeft size={14} /> All Services
        </Link>
        <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-accent-strong mb-6">Outdoor Living</p>
        <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] max-w-4xl">
          Outdoor Living Space Contractor in Monmouth County, NJ
        </h1>
        <p className="mt-8 text-lg text-muted-foreground font-light max-w-2xl leading-relaxed">
          Pergolas, covered decks, porches, screened rooms and outdoor kitchens — planned as one backyard
          instead of a series of separate purchases. Triple 3 Construction LLC builds the structures behind
          those spaces for homeowners across Monmouth County, the Jersey Shore and Long Beach Island.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link to="/project-inquiry" className="btn-charcoal">
            Request a Free Estimate <ArrowRight size={14} />
          </Link>
          <Link to="/projects" className="btn-outline">
            See completed work
          </Link>
        </div>
        <p className="mt-5 text-xs text-muted-foreground font-light">
          Estimates are free and written. There is no pressure to move forward.
        </p>
      </section>

      <section className="container-luxe pb-16 md:pb-20">
        <div className="aspect-[4/3] bg-muted overflow-hidden">
          <Image
            src={OUTDOOR_LIVING_HERO}
            alt="Covered deck, pergola and outdoor kitchen planned as one outdoor living space"
            fittingType="fill"
            className="w-full h-full"
          />
        </div>
      </section>

      {/* What we build */}
      <section className="container-luxe pb-8">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">What we build outdoors</h2>
          <p className="text-muted-foreground font-light leading-relaxed">
            Most backyards end up with one structure and a list of things that never got added — lighting that
            was never blocked for, a kitchen that could not be plumbed, a roof that had nothing structural to
            sit on. Planning the whole space at once avoids that. Below is what we build, and how each piece
            relates to the next.
          </p>
        </div>
      </section>

      {OUTDOOR_LIVING_SECTIONS.map((section, i) => (
        <section key={section.id} id={section.id} className={i % 2 === 1 ? "bg-secondary section-luxe" : "section-luxe"}>
          <div className="container-luxe">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-7">
                <span className="font-mono text-xs text-accent-strong tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold tracking-tight mt-3 mb-5">{section.title}</h2>
                <p className="text-muted-foreground font-light leading-relaxed mb-8">{section.lead}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  {section.bullets.map((b) => (
                    <div key={b} className="flex items-start gap-3 py-2.5 border-b border-border">
                      <Check size={15} className="text-accent mt-0.5 shrink-0" strokeWidth={1.5} />
                      <span className="text-sm font-light">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
              {section.image && (
                <div className="lg:col-span-5">
                  <div className="aspect-[4/3] bg-muted overflow-hidden">
                    <Image src={section.image} alt={section.image_alt || section.title} fittingType="fill" className="w-full h-full" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      ))}

      {/* Structural differentiator */}
      <section className="bg-foreground text-background section-luxe">
        <div className="container-luxe max-w-4xl">
          <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-champagne mb-4">The structure behind the space</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] mb-8">
            A roofed structure is a load problem, not a decorating problem.
          </h2>
          <p className="text-background/80 font-light leading-relaxed mb-6">
            A pergola, a covered deck and a screened porch all transfer weight somewhere — down through posts
            and beams into footings, and sideways into the house. Decks that were never framed for a roof
            often cannot take one without being opened up first. That is a framing question, and framing is
            what this company does every day.
          </p>
          <p className="text-background/80 font-light leading-relaxed mb-10">
            Before quoting a covered structure, we look at joist size and spacing, how the deck ties into the
            house, the beam and post arrangement, and how the footings are set. If a section has to be
            reinforced or rebuilt to carry the roof, you hear it up front — in writing — rather than discovering
            it halfway through the build.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              ["Framing first", "Joists, beams, posts and footings checked before the roof is priced."],
              ["Details that last", "Flashing, ventilation and fasteners detailed for salt air and freeze-thaw."],
              ["Trades coordinated", "Gas, electrical and plumbing handled by qualified licensed subcontractors."],
            ].map(([title, desc]) => (
              <div key={title} className="border-t border-white/20 pt-5">
                <p className="text-[11px] font-bold tracking-[0.2em] uppercase mb-2">{title}</p>
                <p className="text-sm text-background/70 font-light leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials + permits */}
      <section className="container-luxe section-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-accent-strong mb-4">Materials</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-5">What holds up in New Jersey</h2>
            <p className="text-muted-foreground font-light leading-relaxed mb-4">
              Shore humidity, salt air and freeze-thaw cycles finish off outdoor structures faster than
              anything else. Composite decking such as AZEK or Trex carries the walking surface; wood framing
              usually does the structural work, with flashing and spacing detailed so water gets out and the
              structure can dry. Near the water, fasteners and connectors are specified for salt exposure.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              {["AZEK", "Trex", "Composite decking", "Pressure-treated framing", "Marine-grade fasteners", "Beadboard ceilings"].map((m) => (
                <span key={m} className="border border-border px-4 py-2 text-sm font-light">{m}</span>
              ))}
            </div>
            <Link to="/services/azek-vs-trex-vs-wood" className="inline-flex items-center gap-2 mt-8 text-[11px] font-bold tracking-[0.2em] uppercase text-accent-strong hover:text-accent transition-colors">
              AZEK vs. Trex vs. wood compared <ArrowRight size={14} />
            </Link>
          </div>
          <div>
            <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-accent-strong mb-4">Permits</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-5">Permits and inspections</h2>
            <p className="text-muted-foreground font-light leading-relaxed mb-4">
              A roofed structure, or anything that adds structural load, generally requires a building permit
              and inspections. Freestanding pergolas sometimes also need zoning review for height and lot
              coverage. Requirements are set town by town across Monmouth County and differ with the lot, so
              they get checked with your municipality before construction starts.
            </p>
            <p className="text-muted-foreground font-light leading-relaxed mb-4">
              We coordinate applicable documentation and municipal requirements, including construction
              applications, structural details and inspections. Permit requirements vary by municipality,
              property and scope, and permit approval is not guaranteed.
            </p>
            <Link to="/covered-deck-cost-nj" className="inline-flex items-center gap-2 mt-4 text-[11px] font-bold tracking-[0.2em] uppercase text-accent-strong hover:text-accent transition-colors">
              What a covered deck costs in NJ <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Towns */}
      <section className="bg-secondary section-luxe">
        <div className="container-luxe">
          <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-accent-strong mb-4">Service area</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Outdoor living projects across Monmouth County and the Shore
          </h2>
          <p className="text-muted-foreground font-light max-w-2xl mb-10">
            Each town below has its own page covering local housing stock, site conditions, permitting and the
            kind of outdoor work that fits there.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1">
            {OUTDOOR_LIVING_TOWNS.map((t) => (
              <Link key={t.slug} to={`/locations/${t.slug}`} className="text-sm font-light border-b border-border py-3 hover:text-accent transition-colors">
                {t.name}
              </Link>
            ))}
          </div>
          <Link to="/locations" className="inline-flex items-center gap-2 mt-10 text-[11px] font-bold tracking-[0.2em] uppercase text-accent-strong hover:text-accent transition-colors">
            All New Jersey service areas <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FAQs */}
      <section className="container-luxe section-luxe">
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10">Outdoor living FAQs</h2>
        <OutdoorFaqList faqs={OUTDOOR_LIVING_FAQS} />
      </section>

      {/* Related services */}
      <section className="container-luxe pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Link to="/services/decks-outdoor-carpentry" className="group border border-border p-8 hover:bg-secondary transition-colors">
            <h3 className="font-heading text-lg font-semibold mb-2 group-hover:text-accent transition-colors">Decks &amp; outdoor carpentry</h3>
            <p className="text-sm text-muted-foreground font-light">Deck framing, replacement, porches, pergolas, stairs and railings.</p>
          </Link>
          <Link to="/services/residential-framing" className="group border border-border p-8 hover:bg-secondary transition-colors">
            <h3 className="font-heading text-lg font-semibold mb-2 group-hover:text-accent transition-colors">Residential framing</h3>
            <p className="text-sm text-muted-foreground font-light">The structural work behind additions, second stories and covered structures.</p>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-foreground text-background py-20 md:py-24">
        <div className="container-luxe text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
            Planning an outdoor living space?
          </h2>
          <p className="mt-6 text-sm md:text-base font-light text-background/80 max-w-xl mx-auto">
            Tell us about the yard, the house and how you want to use the space. Estimates are free and there
            is no pressure to move forward.
          </p>
          <Link to="/project-inquiry" className="inline-flex items-center gap-2 mt-10 bg-white text-foreground px-9 py-4 text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-champagne transition-all">
            Start a Project Inquiry <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <p className="container-luxe pt-8 pb-8 text-[10px] text-muted-foreground font-light tracking-wide">
        Imagery on this page may be AI-generated for illustration purposes, and some images may not depict actual completed Triple 3 Construction LLC projects.
      </p>
    </div>
  );
}